import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useCannotBecause } from '@/lib/permissions';

import { ApiError, api, type ImportReport } from '@/lib/api';
import { useLabel, useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * CSV import, as the four steps `plan-beta` B3 specifies: **upload → preview
 * parsed rows → map columns → dry-run → commit. Never a blind upload.**
 *
 * THE PREVIEW IS THE SERVER'S, not a second parser in the browser. Uploading
 * runs a dry run, and everything shown below — the row-by-row plan, the errors,
 * the headers that were not recognised — is that response. A client-side CSV
 * parser would be a second reading of the same file, free to disagree with the
 * one that decides what gets written, and the operator would have no way to know
 * which they were looking at.
 *
 * WHAT `unmapped` IS FOR. A header this platform does not read is a column
 * silently ignored, and a silently ignored `prompt_ar` is an import that blanks
 * every Arabic prompt in the file. So an unrecognised header is surfaced as a
 * decision — map it, or accept that it is dropped — rather than absorbed.
 */

const IGNORE = '__ignore__';

function ActionBadge({ action }: { action: string }) {
  const label = useLabel();
  const tone = action === 'error' ? 'destructive' : action === 'create' ? 'default' : 'secondary';
  return <Badge variant={tone}>{label('import.action', action)}</Badge>;
}

export function ImportDialog({ onImported }: { onImported: () => void }) {
  const t = useT();
  const [csv, setCsv] = useState<string | null>(null);
  const [filename, setFilename] = useState('');
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  // R3. Only the APPLY is gated. A dry run writes nothing and is the most
  // useful thing an analyst can do with a file someone sent them — refusing it
  // would remove the one step that makes the import safe.
  const cannotAuthor = useCannotBecause('items:author');

  const run = useMutation({
    mutationFn: ({ text, dryRun }: { text: string; dryRun: boolean }) =>
      api.importQuestionsCsv(text, dryRun, Object.keys(columnMap).length ? columnMap : undefined),
    onSuccess: (result) => {
      setReport(result);
      setError(null);
      if (!result.dryRun) {
        notify.success(
          t('import.applied'),
          t('import.applied.detail', { created: result.created, updated: result.updated }),
        );
        onImported();
      }
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('portability.importFailed')),
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    setFilename(file.name);
    setCsv(text);
    setColumnMap({});
    setReport(null);
    run.mutate({ text, dryRun: true });
  };

  const remap = (header: string, canonical: string) => {
    const next = { ...columnMap };
    if (canonical === IGNORE) delete next[header];
    else next[header] = canonical;
    setColumnMap(next);
    if (csv) run.mutate({ text: csv, dryRun: true });
  };

  const clean = report !== null && report.errors.length === 0;
  const unresolved = report?.unmapped.filter((h) => !columnMap[h]) ?? [];

  return (
    <DialogContent className="max-h-[90vh] sm:max-w-3xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{t('import.title')}</DialogTitle>
        <DialogDescription>{t('import.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="csvFile">{t('import.csvFile')}</Label>
          <input
            id="csvFile"
            type="file"
            accept=".csv,text/csv"
            className="text-sm"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
          {filename && <p className="text-muted-foreground text-xs">{filename}</p>}
        </div>

        {run.isPending && (
          <p className="text-muted-foreground text-sm">{t('import.checking')}</p>
        )}

        {/* Step 3 — map the headers this platform does not recognise. */}
        {report && report.unmapped.length > 0 && (
          <div className="grid gap-3 rounded-md border p-3">
            <p className="text-sm font-medium">
              {t('import.unrecognised', { count: report.unmapped.length })}
            </p>
            <p className="text-muted-foreground text-xs">{t('import.unrecognised.hint')}</p>
            {report.unmapped.map((header) => (
              <div key={header} className="grid items-center gap-2 sm:grid-cols-2">
                <span className="font-mono text-xs" dir="ltr">{header}</span>
                <Select value={columnMap[header] ?? IGNORE} onValueChange={(v) => remap(header, v)}>
                  <SelectTrigger aria-label={t('import.mapColumn', { header })}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={IGNORE}>{t('import.ignoreColumn')}</SelectItem>
                    {report.knownColumns.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {report && (
          <div className="grid gap-3">
            <div className="flex flex-wrap gap-4 text-sm">
              <span>{t('portability.toCreate', { count: report.wouldCreate + report.created })}</span>
              <span>{t('portability.toUpdate', { count: report.wouldUpdate + report.updated })}</span>
              <span className={report.errors.length ? 'text-destructive' : undefined}>
                {t('import.problems', { count: report.errors.length })}
              </span>
            </div>

            {report.errors.length > 0 && (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  <p className="mb-2">{t('import.allOrNothing')}</p>
                  <ul className="list-disc ps-4">
                    {report.errors.slice(0, 12).map((e, i) => (
                      <li key={`${e.row}-${e.path}-${i}`}>
                        {t('import.rowError', {
                          row: e.row + 1,
                          path: e.path ? ` · ${e.path}` : '',
                          message: e.message,
                        })}
                      </li>
                    ))}
                  </ul>
                  {report.errors.length > 12 && (
                    <p className="mt-2">{t('import.andMore', { more: report.errors.length - 12 })}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Step 2 — the parsed rows, before any of them are applied. */}
            <div className="max-h-56 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 text-start font-medium">{t('import.column.row')}</th>
                    <th className="p-2 text-start font-medium">{t('import.column.item')}</th>
                    <th className="p-2 text-start font-medium">{t('portability.column.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.slice(0, 100).map((r) => (
                    <tr key={r.row} className="border-t">
                      <td className="p-2">{r.row + 1}</td>
                      <td className="p-2 font-mono text-xs" dir="ltr">
                        {r.itemId ?? t('import.newRow')}
                      </td>
                      <td className="p-2"><ActionBadge action={r.action} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {report.rows.length > 100 && (
              <p className="text-muted-foreground text-xs">
                {t('import.truncated', { total: report.rows.length })}
              </p>
            )}
          </div>
        )}

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter className="gap-2">
        {unresolved.length > 0 && (
          <p className="text-muted-foreground me-auto self-center text-xs">
            {t('import.willIgnore', { count: unresolved.length })}
          </p>
        )}
        <Button
          disabled={!clean || run.isPending || !csv || report?.dryRun === false || cannotAuthor !== null}
          title={cannotAuthor ?? undefined}
          onClick={() => csv && run.mutate({ text: csv, dryRun: false })}
        >
          {t(report?.dryRun === false ? 'portability.applied' : 'portability.apply')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
